import { Box, Table, Tbody, Td, Text, Tr, useToken } from "@chakra-ui/react";
import { ReactNode, useRef } from "react";
import { FormattedMessage } from "react-intl";

import { RanaLoading } from "@nens/ranalib";
import { useRect } from "@nens/react/hooks";

export interface DetailRow {
  id?: string;
  name: string;
  value: ReactNode;
  action?: ReactNode;
}

interface DetailTableProps {
  rows: DetailRow[];
  isLoading?: boolean;
}

export function DetailTable({ rows, isLoading = false }: DetailTableProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { width: tableWidth } = useRect(wrapperRef);
  const [firstRowWidth, lastRowWidth] = useToken("sizes", [44, 4]);
  const hasActions = rows.some((row) => row.action !== undefined);

  return (
    <Box
      ref={wrapperRef}
      w="full"
      overflow="hidden"
      // only rounded corners for the outer borders
      rounded="base"
      borderWidth="base"
      borderColor="brand.border"
      bg="brand.bg"
    >
      <Table variant="grid" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <Tbody>
          {isLoading ? (
            <Tr>
              <Td borderStyle="none" pl={4} colSpan={hasActions ? 3 : 2}>
                <RanaLoading isCentered py={4} />
              </Td>
            </Tr>
          ) : (
            rows.map((detail, idx) => {
              const isFirstRow = idx === 0;

              return (
                <Tr key={detail.id ?? detail.name}>
                  <Td
                    borderTopStyle={isFirstRow ? "none" : undefined}
                    borderBottomStyle="none"
                    borderLeftStyle="none"
                    // ensure for all tables that the first column has the same width regardless of the content
                    width={firstRowWidth}
                    minW={firstRowWidth}
                  >
                    <Text
                      position="absolute"
                      top="1/2"
                      transform="translateY(-50%)"
                      // these are needed for correct word break behavior
                      overflowWrap="anywhere"
                      insetInline="0"
                      p={4}
                    >
                      {detail.name}
                    </Text>
                  </Td>
                  <Td
                    borderStyle="none"
                    borderTopStyle={isFirstRow ? undefined : "solid"}
                    pl={4}
                    alignContent="center"
                    maxW={`calc(${tableWidth}px - ${firstRowWidth} - ${
                      hasActions ? lastRowWidth : 0
                    })`}
                  >
                    {detail.value != null && detail.value !== "" ? (
                      detail.value
                    ) : (
                      <Text fontWeight={600} fontStyle="italic">
                        <FormattedMessage id="error.not_available" defaultMessage="Not available" />
                      </Text>
                    )}
                  </Td>
                  {hasActions && (
                    <Td
                      borderStyle="none"
                      borderTopStyle={isFirstRow ? undefined : "solid"}
                      pl={4}
                      textAlign="right"
                      alignContent="center"
                      width={lastRowWidth}
                    >
                      {detail.action}
                    </Td>
                  )}
                </Tr>
              );
            })
          )}
        </Tbody>
      </Table>
    </Box>
  );
}
